import { Injectable } from '@angular/core';
import { PouchDB } from '../db/pouchdb-init';
import { BaseDocumentService } from './base-document.service';
import { BaseDocument } from '../types';

/**
 * Spezialisierung von BaseDocumentService für Structure-Datenbanken.
 * Structure-DBs bilden die Domänen-Hierarchie einer App ab (z.B. Forstbetriebe, Abteilungen).
 *
 * Im Gegensatz zu BaseDataService:
 * - Kein DocumentHandler (einfachere Dokumente)
 * - Kein räumliches Filtering
 * - Lokale DB pro Projekt: {structureName}-{projectId}
 * - Download wird durch Project-Checkout getriggert
 *
 * Subklassen müssen implementieren:
 * - structureName: Name der Structure (z.B. 'forstbetriebe')
 * - remoteDbName: Name der Remote-DB (z.B. 'fe-forstbetriebe')
 * - getDocIdsForProject(): Welche Docs für ein Projekt heruntergeladen werden
 * - storage, oms, ps: Service-Getter (von BaseDocumentService)
 * - projectSignal, userOfflineModeSignal, networkOnlineSignal (von BasePouchDbService)
 */
@Injectable()
export abstract class BaseStructureService<T extends BaseDocument = BaseDocument>
	extends BaseDocumentService<T> {

	/** Name dieser Structure (z.B. 'forstbetriebe', 'abteilungen') */
	abstract get structureName(): string;

	/**
	 * Lokaler DB-Name: {structureName}-{projectId}.
	 * Jedes Projekt bekommt seine eigene lokale Kopie der Structure-Daten.
	 */
	override get localDbName(): string {
		const projectId = this.ps?.project();
		return projectId ? `${this.structureName}-${projectId}` : '';
	}

	/**
	 * Bestimmt welche Dokument-IDs für ein Projekt heruntergeladen werden sollen.
	 * App-spezifisch: z.B. ForstbetriebeService gibt project.forstbetriebIds zurück.
	 *
	 * Darf die Remote-DB abfragen um IDs zu ermitteln (z.B. über Views).
	 */
	abstract getDocIdsForProject(projectDoc: any): Promise<string[]>;

	/**
	 * Lädt alle Dokumente direkt aus der Remote-DB (ohne lokale Replikation).
	 * Wird z.B. für die Zuweisungs-Checkboxen im Projekt-Dialog benötigt.
	 * Initialisiert remoteDb bei Bedarf (Structure-Services rufen _initDatabases() nicht selbst auf).
	 */
	async getAllFromRemote(): Promise<T[]> {
		if (!this.remoteDb) {
			await this.ensureRemoteDb();
		}
		if (!this.remoteDb) return [];
		const result = await this.remoteDb.allDocs({ include_docs: true });
		return result.rows
			.filter(row => !row.id.startsWith('_design/'))
			.map(row => row.doc as T);
	}

	/**
	 * Stellt sicher, dass remoteDb initialisiert ist.
	 * Structure-Services rufen _initDatabases() nicht auf, da sie keine eigene
	 * Lifecycle-Verwaltung haben. Diese Methode initialisiert remoteDb bei Bedarf.
	 */
	override async ensureRemoteDb(): Promise<boolean> {
		if (this.remoteDb) return true;
		return new Promise<boolean>((resolve) => {
			this._initRemoteDatabase(() => resolve(!!this.remoteDb));
		});
	}

	// ========== Lifecycle: Download / Sync / Discard ==========

	/**
	 * Lädt Structure-Daten für einen Project-Checkout herunter.
	 * Erstellt eine lokale DB und repliziert die relevanten Dokumente.
	 */
	async downloadForProject(projectDoc: any): Promise<void> {
		if (!this.remoteDb) {
			await this.ensureRemoteDb();
		}
		if (!this.remoteDb) {
			console.warn(`[${this.structureName}] Keine Remote-DB – überspringe Download`);
			return;
		}

		const docIds = await this.getDocIdsForProject(projectDoc);
		const dbName = this.localDbName;
		if (!dbName) return;

		this.isBusy = true;

		try {
			if (!this.localDb) {
				this.localDb = new PouchDB(dbName);
			}

			if (docIds.length > 0) {
				await new Promise<void>((resolve, reject) => {
					this.localDb!.replicate.from(this.remoteDb!, {
						batch_size: 10,
						live: false,
						retry: true,
						style: 'main_only',
						doc_ids: docIds
					} as any)
						.on('change', (result: any) => this.setProgress(result))
						.on('complete', () => {
							this.loadingProgress = 100;
							resolve();
						})
						.on('error', (err: any) => reject(err));
				});
			}

			this._documentsDownloaded = true;
			this.documentsDownloadCompleted.emit();
		} catch (err) {
			this.loadingProgress = 0;
			console.error(`[${this.structureName}] Fehler beim Download:`, err);
		} finally {
			this.isBusy = false;
		}
	}

	/**
	 * Synchronisiert lokale Structure-Änderungen zurück zur Remote-DB
	 * und zerstört die lokale DB.
	 * Wird beim Checkin aufgerufen.
	 */
	async syncAndDiscard(): Promise<void> {
		if (this.localDb && this.remoteDb) {
			try {
				await new Promise<void>((resolve, reject) => {
					PouchDB.replicate(this.localDb!, this.remoteDb!, { batch_size: 10 })
						.on('complete', () => resolve())
						.on('error', (err: any) => reject(err));
				});
			} catch (err) {
				console.error(`[${this.structureName}] Fehler beim Sync:`, err);
			}
		}

		if (this.localDb) {
			try {
				await this.localDb.destroy();
			} catch (err) {
				console.warn(`[${this.structureName}] Fehler beim Zerstören der lokalen DB:`, err);
			}
			this.localDb = null;
		}
		this._documentsDownloaded = false;
	}

	/**
	 * Verwirft die lokale Structure-DB ohne Synchronisation.
	 * Wird beim Discard aufgerufen.
	 */
	override async discardLocal(): Promise<void> {
		if (this.localDb) {
			try {
				await this.localDb.destroy();
			} catch (err) {
				console.warn(`[${this.structureName}] Fehler beim Löschen der lokalen DB:`, err);
			}
		}
		this.localDb = null;
		this._documentsDownloaded = false;
		this.downloadedDocumentsDiscarded.emit();
		this.discarded.emit();
	}

	// ========== Default-Implementierungen für BasePouchDbService-Hooks ==========
	// Reduziert Boilerplate in konkreten Structure-Services.

	protected onInitializeRemoteDatabase(db: PouchDB.Database): Promise<void> {
		return Promise.resolve();
	}

	protected onInitializeLocalDatabase(db: PouchDB.Database): Promise<void> {
		return Promise.resolve();
	}

	protected onDatabasesInitialized(): void {
		this.isInitialized = true;
		this.initialized.emit();
	}

	protected onDatabasesClosed(): void {
		// Default: keine Aktion
	}

	protected async shouldKeepEmptyLocalDb(dbName: string): Promise<boolean> {
		return this.oms?.userOfflineMode() ?? false;
	}
}
